"use client";

import { useState, useEffect } from "react";
import TeacherInsight from "./components/TeacherInsight";
import "./TeacherDashboard.css"; // Import the new CSS file
import { API_URL } from "./config";
import { getProgressInsights } from "./services/topicsService";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts"; //visualization

function TeacherDashboard() {
  const [assessments, setAssessments] = useState([]);
  const [assessmentId, setAssessmentId] = useState(""); // <-- start empty
  const [questions, setQuestions] = useState([]);
  const [selectedQuestion, setSelectedQuestion] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [overviewData, setOverviewData] = useState([]);
  const [progressSummary, setProgressSummary] = useState(null);
  const [progressStatus, setProgressStatus] = useState(null);
  const [progressLoading, setProgressLoading] = useState(false);

  // Fetch available assessments when page loads
  useEffect(() => {
    async function fetchAssessments() {
      try {
        setLoading(true);
        const response = await fetch(`${API_URL}/api/assessments`); // <-- backend route
        if (!response.ok) {
          throw new Error("Failed to load assessments");
        }
        const data = await response.json();
        setAssessments(data.assessments);
        if (data.assessments.length > 0) {
          setAssessmentId(data.assessments[0].id); // <-- auto-select first assessment
        }
      } catch (error) {
        console.error("Error fetching assessments:", error);
        setError("Failed to load assessments. Please try again later.");
      } finally {
        setLoading(false);
      }
    }
    fetchAssessments();
  }, []);

  // Fetch questions whenever assessmentId changes
  useEffect(() => {
    if (!assessmentId) return;
    async function fetchQuestionsAndOverview() {
      try {
        setLoading(true);
        setError("");

        // Fetch questions first
        const response = await fetch(
          `${API_URL}/api/assessment/${assessmentId}/questions`
        );
        if (!response.ok) {
          throw new Error("Failed to load questions");
        }
        const data = await response.json();
        setQuestions(data.questions);
        setSelectedQuestion(""); // reset selected question when changing assessments

        // Fetch overview data - this might return empty data if no submissions
        try {
          const ovRes = await fetch(
            `${API_URL}/api/assessment-analysis/${assessmentId}`
          );
          if (ovRes.ok) {
            const ovData = await ovRes.json();
            setOverviewData(ovData.data || []);
          } else {
            // If analysis fails, just set empty data - don't throw error
            console.warn("No submission data available for analysis");
            setOverviewData([]);
          }
        } catch (analysisError) {
          console.warn("Analysis data not available:", analysisError);
          setOverviewData([]);
        }
        await fetchProgressInsights(assessmentId, false);
      } catch (error) {
        console.error("Error fetching questions:", error);
        setError("Failed to load questions. Please try again later.");
        setQuestions([]);
        setOverviewData([]);
      } finally {
        setLoading(false);
      }
    }
    fetchQuestionsAndOverview();
  }, [assessmentId]);

  // Custom tooltip for the chart
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="chart-tooltip">
          <p className="tooltip-label">{`Question: ${label}`}</p>
          {payload.map((entry, index) => (
            <p
              key={index}
              className="tooltip-item"
              style={{ color: entry.color }}
            >
              {`${entry.name}: ${entry.value}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Calculate summary statistics
  const summaryStats = overviewData.reduce(
    (acc, item) => ({
      totalQuestions: acc.totalQuestions + 1,
      totalSubmissions: acc.totalSubmissions + item.totalSubmissions,
      totalCorrect: acc.totalCorrect + item.correctSubmissions,
      totalCompilationErrors:
        acc.totalCompilationErrors + item.compilationErrors,
      totalSemanticErrors: acc.totalSemanticErrors + item.semanticErrors,
    }),
    {
      totalQuestions: 0,
      totalSubmissions: 0,
      totalCorrect: 0,
      totalCompilationErrors: 0,
      totalSemanticErrors: 0,
    }
  );

  const getConceptDetails = (label) => {
    if (!progressSummary?.concept_progress) return null;
    const direct = progressSummary.concept_progress[label];
    if (direct) return direct;
    const variants = [
      label?.toUpperCase?.(),
      label?.toLowerCase?.(),
      label?.trim?.(),
    ].filter(Boolean);
    for (const variant of variants) {
      if (variant && progressSummary.concept_progress[variant]) {
        return progressSummary.concept_progress[variant];
      }
    }
    const fallback = Object.values(progressSummary.concept_progress).find((entry) =>
      Array.isArray(entry.metadata?.original_labels) &&
      entry.metadata.original_labels.includes(label)
    );
    return fallback || null;
  };

  const fetchProgressInsights = async (id, triggerTracking = false) => {
    if (!id) {
      setProgressSummary(null);
      setProgressStatus(null);
      return;
    }
    try {
      setProgressLoading(true);
      if (triggerTracking) {
        await fetch(`${API_URL}/api/knowledge-graph/track-progress`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ assessment_id: id }),
        });
      }
      const res = await getProgressInsights(id);
      if (res.status === "success") {
        setProgressSummary(res.data);
        setProgressStatus(triggerTracking ? "fresh" : "cached");
      } else {
        setProgressSummary(null);
        setProgressStatus(res.status);
      }
    } catch (err) {
      console.error("Failed to load progress insights", err);
      setProgressSummary(null);
      setProgressStatus("error");
    } finally {
      setProgressLoading(false);
    }
  };

  return (
    <div className="teacher-dashboard-container">
      <header className="dashboard-header">
        <h1>Teacher Dashboard</h1>
        <p>
          View aggregated insights and recommendations based on student
          performance
        </p>
      </header>

      <div className="dashboard-content">
        {/* Assessment Selector - Add no-hover class */}
        <div className="dashboard-card no-hover">
          <label htmlFor="assessmentSelect" className="selector-label">
            Select an Assessment
          </label>
          <select
            id="assessmentSelect"
            className="assessment-select"
            value={assessmentId}
            onChange={(e) => setAssessmentId(e.target.value)}
            disabled={loading}
          >
            <option value="">-- Select Assessment --</option>
            {assessments.map((a) => (
              <option key={a.id} value={a.id}>
                {a.title}
              </option>
            ))}
          </select>
        </div>

        {/* Assessment Overview Section */}
        {assessmentId && (
          <div className="dashboard-card overview-section no-hover">
            <div className="overview-header">
              <h2>Assessment Overview</h2>
              {overviewData.length > 0 && (
                <div className="summary-stats">
                  <div className="stat-item">
                    <span className="stat-number">
                      {summaryStats.totalQuestions}
                    </span>
                    <span className="stat-label">Questions</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-number">
                      {summaryStats.totalSubmissions}
                    </span>
                    <span className="stat-label">Total Submissions</span>
                  </div>
                  <div className="stat-item correct">
                    <span className="stat-number">
                      {summaryStats.totalCorrect}
                    </span>
                    <span className="stat-label">Correct</span>
                  </div>
                  <div className="stat-item semantic-error">
                    <span className="stat-number">
                      {summaryStats.totalSemanticErrors}
                    </span>
                    <span className="stat-label">Semantic Errors</span>
                  </div>
                  <div className="stat-item compilation-error">
                    <span className="stat-number">
                      {summaryStats.totalCompilationErrors}
                    </span>
                    <span className="stat-label">Compilation Errors</span>
                  </div>
                </div>
              )}
            </div>

            {/* Show message when assessment is selected but no submissions yet */}
            {questions.length > 0 && overviewData.length === 0 && !loading && (
              <div className="no-submissions-message">
                <div className="no-submissions-icon">📊</div>
                <h3>No Student Submissions Yet</h3>
                <p>
                  Students haven't submitted any solutions for this assessment
                  yet. Insights and analytics will appear here once students
                  start submitting their work.
                </p>
              </div>
            )}

            {/* Overview Data Visualization */}
            {overviewData.length > 0 && (
              <div className="chart-container">
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart
                    data={overviewData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      dataKey="questionId"
                      interval={0}
                      tickFormatter={(str) => {
                        // Truncate long question IDs
                        const short = str.replace(/\s+.*$/, "");
                        return short.length > 10
                          ? `${short.slice(0, 10)}...`
                          : short;
                      }}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                      fontSize={12}
                    />
                    <YAxis fontSize={12} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar
                      dataKey="correctSubmissions"
                      name="Correct Submissions"
                      fill="#4caf50"
                      stackId="submissions"
                    />
                    <Bar
                      dataKey="semanticErrors"
                      name="Semantic Errors"
                      fill="#ff9800"
                      stackId="submissions"
                    />
                    <Bar
                      dataKey="compilationErrors"
                      name="Compilation Errors"
                      fill="#f44336"
                      stackId="submissions"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        )}

        {assessmentId && (
          <div className="dashboard-card progress-section">
            <div className="progress-header">
              <h2>Concept Insights</h2>
              <div className="progress-actions">
                {progressStatus === "cached" && (
                  <span className="status-tag cached">cached</span>
                )}
                {progressStatus === "error" && (
                  <span className="status-tag error">refresh failed</span>
                )}
                <button
                  className="refresh-button"
                  disabled={progressLoading}
                  onClick={() => fetchProgressInsights(assessmentId, true)}
                >
                  {progressLoading ? "Refreshing…" : "Refresh Progress"}
                </button>
              </div>
            </div>
            {progressSummary ? (
              <div className="progress-summary">
                <div className="summary-line">
                  <strong>Students:</strong> {progressSummary.total_students || 0}
                  <span className="summary-metric">
                    Avg {progressSummary.class_summary?.average_completion ?? 0}%
                  </span>
                  <span className="summary-metric">
                    Median {progressSummary.class_summary?.median_completion ?? 0}%
                  </span>
                  {progressSummary.generated_at && (
                    <span className="generated-at">
                      {new Date(progressSummary.generated_at).toLocaleString()}
                    </span>
                  )}
                </div>

                {progressSummary.class_summary?.top_concept_gaps?.length > 0 && (
                  <div className="progress-block">
                    <h3>Top Concept Gaps</h3>
                    <ul>
                      {progressSummary.class_summary.top_concept_gaps.map((gap) => (
                        <li key={gap.concept}>
                          <span className="concept-name">{gap.concept}</span>
                          <span className="concept-metric">
                            {gap.percentage}% ({gap.completed}/{gap.total_possible})
                          </span>
                          {(() => {
                            const details = getConceptDetails(gap.concept);
                            if (!details?.metadata) return null;
                            const { metadata } = details;
                            return (
                              <div className="concept-meta">
                                {metadata.status && (
                                  <span>Status: {metadata.status}</span>
                                )}
                                {metadata.source && (
                                  <span>Source: {metadata.source}</span>
                                )}
                              </div>
                            );
                          })()}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {progressSummary.student_summary?.top_students?.length > 0 && (
                  <div className="progress-block">
                    <h3>Top Students</h3>
                    <ul>
                      {progressSummary.student_summary.top_students.map((student) => (
                        <li key={student.net_id}>
                          <span className="student-name">{student.net_id}</span>
                          <span className="concept-metric">
                            {student.mastery_percentage}% mastered
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {progressSummary.needs_review_concepts?.length > 0 && (
                  <div className="progress-block">
                    <h3>Needs Review</h3>
                    <p className="needs-review-hint">
                      These concepts still require instructor verification or mapping.
                    </p>
                    <ul>
                      {progressSummary.needs_review_concepts.map((concept) => {
                        const details = getConceptDetails(concept);
                        const metadata = details?.metadata || {};
                        return (
                          <li key={concept}>
                            <div className="concept-name">{concept}</div>
                            <div className="concept-meta">
                              {metadata.status && <span>Status: {metadata.status}</span>}
                              {metadata.canonical_id && (
                                <span>ID: {metadata.canonical_id}</span>
                              )}
                              {metadata.wikidata_qid && (
                                <a
                                  href={`https://www.wikidata.org/wiki/${metadata.wikidata_qid}`}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  Wikidata: {metadata.wikidata_qid}
                                </a>
                              )}
                              {metadata.source && <span>Source: {metadata.source}</span>}
                              {Array.isArray(metadata.original_labels) && metadata.original_labels.length > 0 && (
                                <span>Original: {metadata.original_labels.join(", ")}</span>
                              )}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <div className="progress-empty">
                {progressStatus === "not_found"
                  ? "Run progress tracking from the Knowledge Graph page or upload materials to generate insights."
                  : "No progress insights available yet."}
              </div>
            )}
          </div>
        )}

        {/* Question Selector - Add no-hover class */}
        {assessmentId && (
          <div className="dashboard-card no-hover">
            <label htmlFor="questionSelect" className="selector-label">
              Select a Question for Detailed Analysis
            </label>
            <select
              id="questionSelect"
              className="question-select"
              onChange={(e) => setSelectedQuestion(e.target.value)}
              value={selectedQuestion}
              disabled={!assessmentId || loading}
            >
              <option value="">-- Choose a Question --</option>
              {questions.map((q) => (
                <option key={q.id} value={q.id}>
                  {q.title}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="dashboard-card error-card">
            <div className="error-message">{error}</div>
          </div>
        )}

        {/* Loading indicator */}
        {loading && (
          <div className="dashboard-card no-hover">
            <div className="loading-indicator">Loading...</div>
          </div>
        )}

        {/* Show only TeacherInsight if question selected */}
        {selectedQuestion && assessmentId && !loading && (
          <div className="dashboard-card no-hover">
            <TeacherInsight
              assessmentId={assessmentId}
              questionId={selectedQuestion}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default TeacherDashboard;
